import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CSVLink } from 'react-csv';
import { PageHeader } from 'ui/pageHeader';
import { InputSearch } from 'ui/inputs/inputSearch';
import { SecondaryButton } from 'ui/button';
import { ModalBody } from 'ui/modal';
import { colors, spacing, fonts, fontSizes, easing, duration } from 'ui/theme';
import { BasicOption } from 'ui/selects/BasicOption';
import { GridRow } from 'ui/indexPages/tables/grid';
import { Skeleton } from 'ui/skeleton';
import { body2, caption, fontWeights, heading5, link2, monospace2 } from 'ui/theme/typography';
import { pxToRem } from 'utils/convert';
import { Content } from 'modules/campaignIndex/components/CampaignNameCell.styles';

export const IndexPageContainer = styled.div`
  font-family: ${fonts.sansSerif};
`;

export const IndexPageHeader = styled(PageHeader)`
  margin-bottom: ${spacing.double};
  align-items: center;
`;

export const IndexHeaderActionContainer = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const WideInputText = styled(InputSearch)`
  width: ${pxToRem(304)}; /** this is what the campaigns, templates, and journeys uses */
`;

export const TableGridRow = styled(GridRow)`
  align-items: center;
`;

export const MinHeightSkeleton = styled(Skeleton)`
  min-height: ${spacing.doubleHalf};
`;

export const ContentContainer = styled.div`
  ${monospace2}
  border-radius: ${spacing.half};
  border: ${spacing.single} dashed transparent;
  color: ${colors.cyan100};
  display: flex;
  padding: ${spacing.threeQuarter} ${spacing.half};
  position: relative;
  transition: all ${easing.exit} ${duration.short};

  &:hover {
    background: ${colors.backgroundPrimary};
    border: ${spacing.single} dashed ${colors.borderStrong};
    transition: all ${easing.entrance} ${duration.short};
    cursor: pointer;

    & > *:first-child {
      opacity: 1;
    }
  }
`;

export const ContentButtonArea = styled.div`
  flex: 0 0 ${pxToRem(125)};
`;

export const ContentText = styled.div`
  flex: 1 1 auto;
  user-select: none;
  word-break: break-word;
`;

export const ContentCopyToClipboardButton = styled(SecondaryButton)`
  opacity: 0;
  position: absolute;
  right: ${spacing.quarter};
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    & + * > span {
      background: ${colors.green10};
    }
  }
`;

export const UnsavedChangesMessage = styled.div`
  ${caption};
  color: ${colors.textWarning};
  display: flex;
  gap: ${spacing.quarter};
  align-items: center;
`;

export const SnippetEditorActionButtonsWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-end;
  gap: ${spacing.half};
`;

export const LeftColumn = styled.div`
  width: 50%;
`;

export const RightColumn = styled.div`
  width: 50%;
`;

export const ErrorBannerContainer = styled.div`
  margin-top: ${spacing.plusHalf};
  margin-bottom: ${spacing.plusHalf};
`;

export const BannerContainer = styled.div`
  margin-top: ${spacing.double};
`;

export const InputHint = styled.p`
  font-size: ${fontSizes.s12};
  margin-top: ${spacing.half};
  span {
    font-weight: ${fontWeights.bold};
  }
`;

export const TwoColumnWrapper = styled.div`
  display: flex;
  padding-top: ${spacing.double};
  gap: ${spacing.double};
`;

export const EditorContainer = styled.div`
  margin-top: ${spacing.double};
`;

export const FooterContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: ${spacing.plusHalf};
`;

export const FlexModalBody = styled(ModalBody)`
  display: flex;
`;

export const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.plusHalf};
`;

export const InsertSnippetContainer = styled.div`
  display: flex;
`;

export const SnippetDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.plusHalf};
  padding-left: ${spacing.plusHalf};
`;

export const SnippetSectionContainer = styled.section`
  textarea {
    background: transparent;
    min-height: ${spacing.x16};
  }
`;

export const SnippetSearchListContainer = styled.div`
  border-right: 1px solid ${colors.borderPrimary};
  padding-right: ${spacing.plusHalf};
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
  min-height: ${pxToRem(550)};
  min-width: ${pxToRem(280)};
  max-width: ${pxToRem(280)};
`;

export const SnippetListContainer = styled.div`
  height: 100%;
  overflow-y: auto;
`;

export const SnippetListItem = styled(BasicOption)<{ isSelected?: boolean }>`
  ${body2}
  cursor: pointer;
  margin-bottom: ${spacing.quarter};

  ${({ isSelected }) =>
    isSelected &&
    `
    color: ${colors.textLink};
`}
`;

export const SnippetInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const SnippetMetaData = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.quarter};
`;

export const SnippetName = styled(Content)`
  ${heading5}
`;

export const LinkedName = styled(Link)`
  ${link2}
  color: ${colors.textPrimary};
  word-break: break-word;
  text-decoration-color: transparent;
  font-weight: 400;
`;

export const UsageLinkedName = styled(LinkedName)`
  margin-bottom: ${spacing.quarter};
`;

export const Name = styled.span`
  ${body2}
  overflow-wrap: break-word;
`;

export const SnippetReferenceModalBody = styled(ModalBody)`
  background-color: ${colors.backgroundPrimary};
  flex: 1;
  padding-bottom: ${spacing.x4};
`;

export const SnippetReferenceContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SnippetReferenceLeftContainer = styled.div`
  display: flex;
  margin-bottom: ${spacing.plusHalf};
`;

export const StatusDetails = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${spacing.plusHalf};
`;

export const CheckBoxContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const BadgeContainer = styled.div`
  display: flex;
`;

export const SnippetReferenceFilterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: ${spacing.double} 0 ${spacing.plusHalf};
`;

export const StyledCSVLink = styled(CSVLink)`
  margin-left: ${spacing.half};
`;

export const SearchContainer = styled.div`
  margin-left: ${spacing.half};
`;
